<script lang="ts">
  import { onMount } from "svelte";
  import { MapPinned, RefreshCw, RotateCcw, Search } from "lucide-svelte";
  import * as Sidebar from "$lib/components/ui/sidebar/index.js";
  import { Button } from "$lib/components/ui/button";
  import { Card, CardContent, CardHeader, CardTitle } from "$lib/components/ui/card";
  import { Input } from "$lib/components/ui/input";
  import DealerMapCanvas from "$lib/components/dealer-map/dealer-map-canvas.svelte";
  import {
    enqueueBilbasenDealerSync,
    enqueueBilbasenDealerWebsiteRescan,
    getBilbasenDealers
  } from "$lib/services/bilbasen-dealer-service";
  import type { BilbasenDealerMapResponse } from "$lib/types/bilbasen-dealer-map-response";
  import type { UsedCarModuleFilter } from "$lib/types/used-car-module-filter";

  let data: BilbasenDealerMapResponse | null = $state(null);
  let isLoading = $state(true);
  let isQueueing = $state(false);
  let isQueueingRescan = $state(false);
  let errorMessage = $state("");
  let successMessage = $state("");
  let searchTerm = $state("");
  let selectedSupplier = $state("All");
  let selectedIpAddress = $state("All");
  let usedCarModuleFilter = $state<UsedCarModuleFilter>("all");

  const suppliers = $derived(
    Array.from(
      new Set(
        (data?.dealers ?? []).map(
          (dealer) => dealer.websiteSupplier ?? "Unknown"
        )
      )
    ).sort((left, right) => left.localeCompare(right))
  );

  const sharedHostingIps = $derived.by(() => {
    const counts = new Map<string, number>();
    for (const dealer of data?.dealers ?? []) {
      for (const ipAddress of dealer.resolvedIpAddresses) {
        counts.set(ipAddress, (counts.get(ipAddress) ?? 0) + 1);
      }
    }

    return Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
  });

  const filteredDealers = $derived(
    (data?.dealers ?? []).filter((dealer) => {
      const supplier = dealer.websiteSupplier ?? "Unknown";
      const normalizedSearch = searchTerm.trim().toLowerCase();
      const matchesSupplier =
        selectedSupplier === "All" || supplier === selectedSupplier;
      const matchesIpAddress =
        selectedIpAddress === "All" ||
        dealer.resolvedIpAddresses.includes(selectedIpAddress);
      const matchesModule =
        usedCarModuleFilter === "all" ||
        (usedCarModuleFilter === "with" && dealer.hasUsedCarModule) ||
        (usedCarModuleFilter === "without" && !dealer.hasUsedCarModule);
      const matchesSearch =
        normalizedSearch.length === 0 ||
        dealer.name.toLowerCase().includes(normalizedSearch) ||
        dealer.address?.toLowerCase().includes(normalizedSearch) ||
        dealer.websiteUrl?.toLowerCase().includes(normalizedSearch) ||
        dealer.resolvedIpAddresses.some((ipAddress) =>
          ipAddress.toLowerCase().includes(normalizedSearch)
        );

      return matchesSupplier && matchesIpAddress && matchesModule && matchesSearch;
    })
  );

  const mappedFilteredCount = $derived(
    filteredDealers.filter(
      (dealer) => dealer.latitude !== null && dealer.longitude !== null
    ).length
  );

  const usedCarModuleCount = $derived(
    (data?.dealers ?? []).filter((dealer) => dealer.hasUsedCarModule).length
  );

  const loadDealers = async () => {
    isLoading = true;
    errorMessage = "";
    try {
      data = await getBilbasenDealers();
    } catch {
      errorMessage = "Dealer data could not be loaded.";
    } finally {
      isLoading = false;
    }
  };

  const startSync = async () => {
    isQueueing = true;
    errorMessage = "";
    successMessage = "";
    try {
      await enqueueBilbasenDealerSync();
      successMessage =
        "Opdatering af forhandlerlisten er sat i kø. Følg jobbet i Hangfire.";
    } catch {
      errorMessage = "Opdatering af forhandlerlisten kunne ikke sættes i kø.";
    } finally {
      isQueueing = false;
    }
  };

  const startWebsiteRescan = async () => {
    isQueueingRescan = true;
    errorMessage = "";
    successMessage = "";
    try {
      await enqueueBilbasenDealerWebsiteRescan();
      successMessage =
        "Genscanning af hjemmesider er sat i kø. Følg jobbet i Hangfire.";
    } catch {
      errorMessage = "Genscanning af hjemmesider kunne ikke sættes i kø.";
    } finally {
      isQueueingRescan = false;
    }
  };

  const handleSupplierChange = (event: Event) => {
    const target = event.currentTarget;
    if (target instanceof HTMLSelectElement) {
      selectedSupplier = target.value;
    }
  };

  const handleModuleChange = (event: Event) => {
    const target = event.currentTarget;
    if (target instanceof HTMLSelectElement) {
      usedCarModuleFilter = target.value as UsedCarModuleFilter;
    }
  };

  const handleIpAddressChange = (event: Event) => {
    const target = event.currentTarget;
    if (target instanceof HTMLSelectElement) {
      selectedIpAddress = target.value;
    }
  };

  const formatUpdatedAt = (value: string | null | undefined) => {
    if (!value) return "Not yet synchronized";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "Unknown"
      : date.toLocaleString("da-DK");
  };

  onMount(() => {
    void loadDealers();
  });
</script>

<Sidebar.Inset>
  <header class="flex h-16 items-center gap-2 border-b border-border px-4">
    <Sidebar.Trigger class="md:hidden" />
    <MapPinned class="size-5 text-primary" />
    <h2 class="text-lg font-semibold">Bilforhandlere</h2>
  </header>

  <section class="space-y-5 p-6">
    <div class="flex flex-col justify-between gap-3 lg:flex-row lg:items-end">
      <div>
        <h1 class="text-2xl font-semibold">Bilbasen dealer map</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Website supplier, location and used car module across Denmark.
        </p>
      </div>
      <div class="flex flex-wrap items-center justify-end gap-3">
        <span class="text-xs text-muted-foreground">
          Updated: {formatUpdatedAt(data?.lastUpdatedUtc)}
        </span>
        <Button
          variant="outline"
          onclick={startWebsiteRescan}
          disabled={isQueueingRescan ||
            isLoading ||
            !data?.rescanCandidateCount}
          title="Genscan fejlede, manglende og ældre hjemmesidescanninger"
        >
          <RotateCcw class={isQueueingRescan ? "animate-spin" : ""} />
          {isQueueingRescan
            ? "Starter..."
            : `Genscan hjemmesider (${data?.rescanCandidateCount ?? 0})`}
        </Button>
        <Button
          onclick={startSync}
          disabled={isQueueing || isLoading}
          title="Opdater den komplette forhandlerliste fra Bilbasen"
        >
          <RefreshCw class={isQueueing ? "animate-spin" : ""} />
          {isLoading
            ? "Loading..."
            : isQueueing
            ? "Starter..."
            : "Opdater forhandlerliste"}
        </Button>
      </div>
    </div>

    <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm text-muted-foreground">Dealers</CardTitle>
        </CardHeader>
        <CardContent class="text-2xl font-semibold">
          {data?.totalCount ?? 0}
        </CardContent>
      </Card>
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm text-muted-foreground">Shown on map</CardTitle>
        </CardHeader>
        <CardContent class="text-2xl font-semibold">
          {mappedFilteredCount}
        </CardContent>
      </Card>
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm text-muted-foreground">Used car module</CardTitle>
        </CardHeader>
        <CardContent class="text-2xl font-semibold">
          {usedCarModuleCount}
        </CardContent>
      </Card>
      <Card>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm text-muted-foreground">Failed or missing</CardTitle>
        </CardHeader>
        <CardContent class="text-2xl font-semibold">
          {data?.failedOrMissingCount ?? 0}
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardContent class="grid gap-3 pt-6 md:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_220px_220px_220px] xl:items-center">
        <label class="relative">
          <Search class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            class="pl-9"
            placeholder="Search dealer, address, website or IP"
            bind:value={searchTerm}
          />
        </label>

        <select
          class="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={selectedSupplier}
          onchange={handleSupplierChange}
          aria-label="Website supplier"
        >
          <option value="All">All suppliers</option>
          {#each suppliers as supplier (supplier)}
            <option value={supplier}>{supplier}</option>
          {/each}
        </select>

        <select
          class="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={selectedIpAddress}
          onchange={handleIpAddressChange}
          aria-label="Shared hosting IP"
        >
          <option value="All">All hosting IPs</option>
          {#each sharedHostingIps as [ipAddress, count] (ipAddress)}
            <option value={ipAddress}>{ipAddress} ({count})</option>
          {/each}
        </select>

        <select
          class="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={usedCarModuleFilter}
          onchange={handleModuleChange}
          aria-label="Used car module"
        >
          <option value="all">All module statuses</option>
          <option value="with">With Brugtbilsmodulet</option>
          <option value="without">Without Brugtbilsmodulet</option>
        </select>
      </CardContent>
    </Card>

    {#if errorMessage}
      <p class="text-sm text-destructive">{errorMessage}</p>
    {/if}
    {#if successMessage}
      <p class="text-sm text-accent">{successMessage}</p>
    {/if}
    {#if isLoading}
      <div class="flex min-h-[520px] items-center justify-center rounded-lg border border-border bg-card">
        <p class="text-sm text-muted-foreground">Loading dealer map...</p>
      </div>
    {:else if data?.dealers.length === 0}
      <div class="flex min-h-[520px] flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card">
        <MapPinned class="size-10 text-muted-foreground" />
        <p class="font-semibold">No dealers have been synchronized yet.</p>
        <p class="text-sm text-muted-foreground">
          Start the first synchronization and follow it in Hangfire.
        </p>
      </div>
    {:else}
      <DealerMapCanvas dealers={filteredDealers} />
      <p class="text-xs text-muted-foreground">
        Showing {mappedFilteredCount} of {filteredDealers.length} matching dealers.
        Dealers without a geocoded address remain in the total but are not plotted.
      </p>
    {/if}
  </section>
</Sidebar.Inset>
